import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import FormButtons from "@components/fields/FormButtons";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import {
  ExpertRoleCreateDocument,
  ExpertRoleCreateMutation,
  ExpertRoleCreateMutationVariables,
  ExpertRoleRoleEnum,
  GetExpertsBasicDocument,
  GetExpertsBasicQuery,
  GetExpertsBasicQueryVariables,
  GetJourneyWithCareProfileAndEligibilityQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  expertId: string;
};

type ExpertRole = NonNullable<GetJourneyWithCareProfileAndEligibilityQuery["journey"]["expertRoles"]>[number];

type Props = {
  expertRoles: ExpertRole[] | null;
  journeyId: string;
  onCancel?: () => void;
  onSuccess?: () => void;
  skipCancel?: boolean;
};

const InviteExpertForm = ({ expertRoles, journeyId, onCancel, onSuccess, skipCancel = false }: Props) => {
  const defaultValues: FormData = {
    expertId: ""
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const getExpertsQuery = useQuery<GetExpertsBasicQuery, GetExpertsBasicQueryVariables>(GetExpertsBasicDocument);

  useEffect(() => {
    if (!getExpertsQuery.loading && getExpertsQuery.data?.experts.pageInfo.hasNextPage) {
      getExpertsQuery.fetchMore({ variables: { cursor: getExpertsQuery.data?.experts.pageInfo.endCursor ?? "" } });
    }
  }, [getExpertsQuery]);

  const allExperts = useNodesFromConnection(getExpertsQuery.data?.experts);
  const experts = useMemo(
    () =>
      allExperts.filter(
        expert => !expertRoles?.some(role => role.role !== ExpertRoleRoleEnum.backup && role.expert.id === expert.id)
      ),
    [allExperts, expertRoles]
  );

  const expertOptions = useMemo(
    () =>
      experts.map(expert => ({
        label: expert.fullName,
        value: expert.id
      })),
    [experts]
  );

  const [createExpertRole, { client }] = useMutation<ExpertRoleCreateMutation, ExpertRoleCreateMutationVariables>(
    ExpertRoleCreateDocument,
    {
      update: (_store, { data }) => {
        if (data?.expertRoleCreate?.expertRole) {
          client.resetStore();
        }
      }
    }
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      expertId: yup.string().required(intl.formatMessage({ id: "errors.supportingExperts.expertId.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return createExpertRole({
      variables: {
        ...form.getValues(),
        journeyId,
        role: ExpertRoleRoleEnum.team_member
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.expertRoleCreate,
        intl,
        onSuccess: () => {
          if (onSuccess) {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.inviteExpert"
      });
      form.reset();
    });
  }, [createExpertRole, form, journeyId, intl, onSuccess]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className="ion-margin-bottom">
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.expertId}
          label={intl.formatMessage({ id: "forms.supportingExperts.grayceExpert" })}
          name="expertId"
          options={expertOptions}
        />
      </div>

      <FormButtons
        hookForm={form}
        narrowMargin
        onCancel={onCancel}
        saveKey="forms.supportingExperts.addToJourney"
        skipCancel={skipCancel}
      />
    </Form>
  );
};

export default InviteExpertForm;
